import { Container } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type {
  HoldAndSpinEvent,
  MoneyCell,
} from '../../model/slot/HoldAndSpinEvents';
import { cellKey, type HoldAndSpinModel } from '../../model/slot/HoldAndSpinModel';
import { HoldAndSpinBoard, type HoldAndSpinBoardArt } from '../../widgets/slot/HoldAndSpinBoard';
import { MoneyValueChip } from '../../widgets/slot/MoneyValueChip';
import type { SlotMachine } from '../../widgets/slot/SlotMachine';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

/** Where the board goes above the reels: a rect the scene hands over (the
 *  road panel's place, which the board takes during the bonus), or null
 *  to sit it just above the reel frame. */
export type HoldAndSpinBoardSlot = { x: number; y: number; width: number; height: number } | null;

/**
 * Visual driver for the hold-and-spin bonus. Two display layers:
 *
 * <ul>
 *   <li>An overlay {@link Container} added as a child of the {@link SlotMachine}
 *       so the per-cell {@link MoneyValueChip}s share the machine's local
 *       coordinate space (and follow when the machine layouts/scales).
 *   <li>A {@link HoldAndSpinBoard} at scene level above the reels: the
 *       spins left and what the locked coins add up to.
 * </ul>
 *
 * <p>Subscribes to a {@link HoldAndSpinModel} via {@link bind}; the bind/unbind
 * lifecycle is per-bonus-run because the model itself is per-run. The host
 * scene calls {@link bind} before starting the bonus (so it sees the start
 * event) and {@link unbind} plus {@link reset} once it has paid out.
 */
export class HoldAndSpinComponent extends Container implements SceneComponent {
  private readonly machine: SlotMachine;
  private readonly cellOverlay: Container;
  private readonly chips = new Map<string, MoneyValueChip>();
  private readonly board: HoldAndSpinBoard;
  private subscriptions: Disposable[] = [];
  private ctx: SceneLayoutContext | null = null;
  private slot: HoldAndSpinBoardSlot = null;

  constructor(machine: SlotMachine, art: HoldAndSpinBoardArt) {
    super();
    this.machine = machine;

    // Overlay sits inside the machine's coordinate space so chips track
    // the cells across resize / scale. Added as a child of the machine,
    // not this component, so it draws above the reels regardless of
    // scene-level z-ordering.
    this.cellOverlay = new Container();
    this.machine.addChild(this.cellOverlay);

    this.board = new HoldAndSpinBoard(art);
    this.addChild(this.board);
  }

  /** The board and coins as plain data, for the browser tests. */
  get snapshot(): { board: boolean; spinsLeft: number; total: number; coins: number } {
    const board = this.board.snapshot;
    return { board: board.visible, spinsLeft: board.spinsLeft, total: board.total, coins: this.chips.size };
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.placeBoard();
  }

  /** The road panel's rect while it's on screen, or null. */
  setBoardSlot(slot: HoldAndSpinBoardSlot): void {
    this.slot = slot;
    this.placeBoard();
  }

  /**
   * Wire to a fresh {@link HoldAndSpinModel}. Idempotent against the same
   * model instance (re-binding a different model implicitly unbinds the
   * previous one).
   */
  bind(model: HoldAndSpinModel): void {
    this.unbind();
    this.subscriptions.push(
      model.events.on('hold-and-spin-started', (event) => {
        this.handleStarted(event.cells, event.initialRespins, model);
      }),
      model.events.on('hold-and-spin-cells-landed', (event) => {
        this.handleCellsLanded(event.newCells, model);
      }),
      model.events.on('hold-and-spin-respins-changed', () => {
        this.board.setSpins(model.respinsRemaining);
      }),
      model.events.on('hold-and-spin-completed', (event) => {
        this.handleCompleted(event);
      }),
    );
  }

  /** Drop the current model's subscriptions. Idempotent. */
  unbind(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions = [];
  }

  /** Where each locked coin sits on the stage, for coins to fly from. */
  coinSources(): { x: number; y: number }[] {
    return [...this.chips.values()].map((chip) => chip.getGlobalPosition());
  }

  /** Wipe every coin and take the board away, once the bonus has paid. */
  reset(): void {
    for (const chip of this.chips.values()) chip.destroyChip();
    this.chips.clear();
    this.board.hide();
  }

  destroy(): void {
    this.unbind();
    for (const chip of this.chips.values()) chip.destroyChip();
    this.chips.clear();
    this.cellOverlay.destroy({ children: true });
    super.destroy({ children: true });
  }

  // -------- private --------

  private placeBoard(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (this.slot) {
      const { x, y, width, height } = this.slot;
      this.board.setLayout(x, y, width, height);
      return;
    }
    // No road panel: a slim board just above the reel frame, as wide as it
    // (up to a comfortable reading width).
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const width = Math.min(right - left, 460, ctx.width - 8);
    const height = 78;
    const y = Math.max(14, ctx.machineTop - height - 10);
    this.board.setLayout(ctx.centerX - width / 2, y, width, height);
  }

  private handleStarted(
    cells: readonly MoneyCell[],
    initialRespins: number,
    model: HoldAndSpinModel,
  ): void {
    // The trigger coins lock in one by one.
    cells.forEach((cell, index) => this.addChip(cell, index * 90));
    this.placeBoard();
    this.board.show(initialRespins, model.totalWinnings());
  }

  private handleCellsLanded(
    newCells: readonly MoneyCell[],
    model: HoldAndSpinModel,
  ): void {
    newCells.forEach((cell, index) => this.addChip(cell, index * 80));
    this.board.setTotal(model.totalWinnings());
  }

  private handleCompleted(event: HoldAndSpinEvent & { kind: 'hold-and-spin-completed' }): void {
    // Each coin pulses in turn, so the payout reads as all of them counting.
    event.cells.forEach((cell, index) => {
      const chip = this.chips.get(cellKey(cell.col, cell.row));
      if (!chip) return;
      setTimeout(() => {
        if (!chip.destroyed) chip.pulse();
      }, index * 80);
    });
  }

  private addChip(cell: MoneyCell, delayMs: number): void {
    const key = cellKey(cell.col, cell.row);
    if (this.chips.has(key)) return;
    const chip = new MoneyValueChip(cell.value, this.machine.cellSize(), {
      betMultiplier: cell.betMultiplier,
    });
    const center = this.machine.cellCenter(cell.col, cell.row);
    chip.x = center.x;
    chip.y = center.y;
    this.cellOverlay.addChild(chip);
    this.chips.set(key, chip);
    chip.popIn(delayMs);
  }
}
